import "../BitacoraProduccion.css";
import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";

// Material UI Icons
import GrassIcon from '@mui/icons-material/Grass';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ScienceIcon from '@mui/icons-material/Science';
import LayersIcon from '@mui/icons-material/Layers';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import WineBarIcon from '@mui/icons-material/WineBar';
import CancelIcon from '@mui/icons-material/Cancel';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SearchIcon from '@mui/icons-material/Search';
import InboxIcon from '@mui/icons-material/Inbox';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FactCheckIcon from '@mui/icons-material/FactCheck';

export const EQUIPOS_MOLIENDA = [
  "Mazo de madera",
  "Tahona, molino chileno o egipcio",
  "Trapiche",
  "Desgarradora",
  "Tren de molinos",
  "Difusor"
];

export const EQUIPOS_FERMENTACION = [
  "Oquedades en piedra",
  "Oquedades en suelo",
  "Oquedades en tronco",
  "Pieles de animal",
  "Recipientes de barro",
  "Recipientes de madera",
  "Piletas de mampostería",
  "Tanque de acero inoxidable"
];

const renderMoliendaStatusBadge = (status) => {
  const norm = String(status || 'borrador').toLowerCase().trim();
  if (norm === 'aceptada' || norm === 'aceptado' || norm === 'aprobado' || norm === 'aprobada' || norm === 'aprobado_ui') {
    return <span className="badge-status aceptada">Aceptada</span>;
  }
  if (norm === 'en_revision' || norm === 'revision') {
    return <span className="badge-status revision">En Revisión</span>;
  }
  if (norm === 'enviada' || norm === 'enviado' || norm === 'nueva' || norm === 'pendiente' || norm === 'pendiente_ui') {
    return <span className="badge-status enviada">Enviada</span>;
  }
  if (norm === 'rechazada' || norm === 'rechazado' || norm === 'rechazado_ui') {
    return <span className="badge-status rechazada">Rechazada</span>;
  }
  if (norm === 'cancelada' || norm === 'cancelado') {
    return <span className="badge-status cancelada">Cancelada</span>;
  }
  if (norm === 'borrador' || norm === 'borrador_ui') {
    return <span className="badge-status borrador">Borrador</span>;
  }
  return <span className="badge-status borrador">Borrador</span>;
};

const renderModalHeaderStatusBadge = (status) => {
  const norm = String(status || 'borrador').toLowerCase().trim();
  let label = "Borrador";
  let bg = "rgba(255,255,255,0.25)";
  if (norm === 'aceptada' || norm === 'aceptado' || norm === 'aprobado' || norm === 'aprobada' || norm === 'aprobado_ui') {
    label = "Aceptada por UI";
    bg = "#059669";
  } else if (norm === 'en_revision' || norm === 'revision') {
    label = "En Revisión UI";
    bg = "#D97706";
  } else if (norm === 'enviada' || norm === 'enviado' || norm === 'nueva' || norm === 'pendiente' || norm === 'pendiente_ui') {
    label = "Enviada a UI";
    bg = "#2563EB";
  } else if (norm === 'rechazada' || norm === 'rechazado' || norm === 'rechazado_ui') {
    label = "Rechazada (Corrección)";
    bg = "#DC2626";
  } else if (norm === 'cancelada' || norm === 'cancelado') {
    label = "Cancelada";
    bg = "#64748B";
  }
  return (
    <span style={{ fontSize: "11.5px", padding: "3px 10px", borderRadius: "12px", background: bg, color: "#FFFFFF", fontWeight: 700, border: "1px solid rgba(255,255,255,0.4)" }}>
      {label}
    </span>
  );
};


export const getCoccionMoliendaBalance = (coccion, moliendaRecords = [], currentMoliendaId = null) => {
  if (!coccion) return { totalKgCocidos: 0, kgUsados: 0, kgDisponibles: 0, isAgotada: true, pctConsumido: 0, moliendasCount: 0 };

  const totalKgCocidos = Number(
    String(coccion.kgMagueyCocido || coccion.kg_maguey_cocido || coccion.kgMaguey || coccion.kg_maguey || 0)
      .replace(/[^\d.]/g, '')
  ) || 0;

  const noProd = String(coccion.noProduccion || coccion.no_produccion || '').trim();
  const coccionId = String(coccion.rawId || coccion.id || coccion.id_bitacora_coccion || '').trim();

  const matchingMoliendas = (Array.isArray(moliendaRecords) ? moliendaRecords : []).filter(m => {
    if (currentMoliendaId && String(m.rawId || m.id || m.id_bitacora_molienda) === String(currentMoliendaId)) {
      return false;
    }
    const isCancelled = String(m.status || m.estatus || '').toLowerCase().includes('cancelad');
    if (isCancelled) return false;

    const mNoProd = String(m.noProduccion || m.no_produccion || '').trim();
    const mCoccionId = String(m.id_bitacora_coccion || m.idBitacoraCoccion || '').trim();

    return (noProd && mNoProd === noProd) || (coccionId && mCoccionId === coccionId);
  });

  const kgUsados = matchingMoliendas.reduce((acc, m) => {
    const items = Array.isArray(m.items) ? m.items : [];
    const moliendaKg = items.reduce((tAcc, it) => tAcc + (Number(String(it.kgMaguey || it.kg_maguey || 0).replace(/[^\d.]/g, '')) || 0), 0);
    const fallbackKg = Number(String(m.totalKgMaguey || m.total_kg_maguey || 0).replace(/[^\d.]/g, '')) || 0;
    return acc + (moliendaKg > 0 ? moliendaKg : fallbackKg);
  }, 0);

  const kgDisponibles = Math.max(0, totalKgCocidos - kgUsados);
  const isAgotada = totalKgCocidos > 0 ? (kgDisponibles <= 0) : false;
  const pctConsumido = totalKgCocidos > 0 ? Math.min(100, Math.round((kgUsados / totalKgCocidos) * 100)) : 0;

  return {
    totalKgCocidos,
    kgUsados,
    kgDisponibles,
    isAgotada,
    pctConsumido,
    moliendasCount: matchingMoliendas.length
  };
};

const MoliendaSection = ({
  moliendaRecords = [],
  coccionRecords = [],
  pagination,
  moliendaSearch: propSearch,
  onSearchChange: propOnSearchChange,
  onPageChange,
  statusFilter: propStatusFilter,
  onStatusFilterChange,
  isFormOpen,
  setIsFormOpen,
  formData = {},
  setFormData,
  handleSaveMolienda,
  handleCancelMolienda,
  handleRejectMolienda,
  onOpenNewMolienda,
  onOpenNewDestilacion,
  instalaciones = []
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [localSearch, setLocalSearch] = useState("");
  const [localStatusFilter, setLocalStatusFilter] = useState("todos");

  const search = propSearch !== undefined ? propSearch : localSearch;
  const statusFilter = propStatusFilter !== undefined ? propStatusFilter : localStatusFilter;

  const handleSearchChange = (val) => {
    setLocalSearch(val);
    if (propOnSearchChange) propOnSearchChange(val);
  };

  const handleStatusFilterChange = (val) => {
    setLocalStatusFilter(val);
    if (onStatusFilterChange) onStatusFilterChange(val);
  };

  const moliendaItems = useMemo(() => {
    if (Array.isArray(formData?.items) && formData.items.length > 0) {
      return formData.items;
    }
    const today = new Date().toISOString().split('T')[0];
    return [
      {
        id: 1,
        fechaFermentacion: today,
        contenedor: "Tina 01",
        kgMaguey: "",
        equipoMolienda: "",
        equipoFermentacion: "",
        fechaFormulacion: today,
        volumenFormulado: "",
        fechaPrimeraDestilacion: today,
        volumenOrdinario: "",
        volumenMezcal: "",
        pctAlcOrdinario: "",
        pctAlcMezcal: "",
        volumenColas: "",
        pctAlcColas: ""
      }
    ];
  }, [formData?.items]);

  // Filtrado de registros de la tabla
  const filteredRecords = useMemo(() => {
    if (propStatusFilter !== undefined && propSearch !== undefined) {
      return moliendaRecords;
    }
    return moliendaRecords.filter(rec => {
      const normStatus = String(rec.status || rec.statusUI || "borrador").toLowerCase().trim();
      if (statusFilter !== "todos") {
        if (statusFilter === "aceptada") {
          if (normStatus !== "aceptada" && normStatus !== "aceptado" && normStatus !== "aprobado" && normStatus !== "aprobada" && normStatus !== "aprobado_ui") return false;
        } else if (statusFilter === "enviada") {
          if (normStatus !== "enviada" && normStatus !== "enviado" && normStatus !== "revision" && normStatus !== "en_revision" && normStatus !== "nueva" && normStatus !== "pendiente" && normStatus !== "pendiente_ui") return false;
        } else if (statusFilter === "rechazada") {
          if (normStatus !== "rechazada" && normStatus !== "rechazado" && normStatus !== "rechazado_ui") return false;
        } else if (statusFilter === "cancelada") {
          if (normStatus !== "cancelada" && normStatus !== "cancelado") return false;
        } else if (statusFilter === "borrador") {
          if (normStatus !== "borrador") return false;
        }
      }

      if (search) {
        const term = search.toLowerCase();
        const noProdMatch = rec.noProduccion && rec.noProduccion.toLowerCase().includes(term);
        const inspMatch = rec.inspector && rec.inspector.toLowerCase().includes(term);
        const itemMatch = rec.items && rec.items.some(it =>
          (it.contenedor && String(it.contenedor).toLowerCase().includes(term)) ||
          (it.fechaFermentacion && String(it.fechaFermentacion).toLowerCase().includes(term))
        );
        return noProdMatch || inspMatch || itemMatch;
      }

      return true;
    });
  }, [moliendaRecords, statusFilter, search, propStatusFilter, propSearch]);

  const handleEditRecord = (rec) => {
    const isEditable = rec.status === "borrador" || rec.status === "rechazado" || rec.status === "rechazada";
    if (!isEditable) {
      Swal.fire({
        title: "Acción restringida",
        text: "Solo se pueden editar registros que se encuentren en estado BORRADOR o RECHAZADO.",
        icon: "warning",
        confirmButtonColor: "#4A5D23"
      });
      return;
    }
    setFormData({
      id: rec.id || rec.rawId,
      rawId: rec.rawId || rec.id,
      noProduccion: rec.noProduccion || "",
      observaciones: rec.observaciones || "",
      items: (rec.items && Array.isArray(rec.items) && rec.items.length > 0) ? rec.items.map((it, idx) => ({
        id: it.id || (idx + 1),
        fechaFermentacion: it.fechaFermentacion || "",
        contenedor: it.contenedor || `Tina ${idx + 1 < 10 ? '0' + (idx + 1) : idx + 1}`,
        kgMaguey: it.kgMaguey ? String(it.kgMaguey).replace(/[^\d.]/g, '') : "",
        equipoMolienda: it.equipoMolienda || it.equipo_molienda || "",
        equipoFermentacion: it.equipoFermentacion || it.equipo_fermentacion || "",
        fechaFormulacion: it.fechaFormulacion || "",
        tipoFormulacion: it.tipoFormulacion || it.tipo_formulacion || "espontanea",
        metodoFormulacion: it.metodoFormulacion || it.metodo_formulacion || "",
        volumenFormulado: it.volumenFormulado ? String(it.volumenFormulado).replace(/[^\d.]/g, '') : "",
        fechaPrimeraDestilacion: it.fechaPrimeraDestilacion || "",
        volumenOrdinario: it.volumenOrdinario || it.volumenMezcal ? String(it.volumenOrdinario || it.volumenMezcal).replace(/[^\d.]/g, '') : "",
        volumenMezcal: it.volumenOrdinario || it.volumenMezcal ? String(it.volumenOrdinario || it.volumenMezcal).replace(/[^\d.]/g, '') : "",
        pctAlcOrdinario: it.pctAlcOrdinario || it.pctAlcMezcal ? String(it.pctAlcOrdinario || it.pctAlcMezcal).replace(/[^\d.]/g, '') : "",
        pctAlcMezcal: it.pctAlcOrdinario || it.pctAlcMezcal ? String(it.pctAlcOrdinario || it.pctAlcMezcal).replace(/[^\d.]/g, '') : "",
        volumenColas: it.volumenColas ? String(it.volumenColas).replace(/[^\d.]/g, '') : "",
        pctAlcColas: it.pctAlcColas ? String(it.pctAlcColas).replace(/[^\d.]/g, '') : ""
      })) : [
        {
          id: Date.now(),
          fechaFermentacion: new Date().toISOString().split('T')[0],
          contenedor: "Tina 01",
          kgMaguey: "",
          equipoMolienda: "",
          equipoFermentacion: "",
          fechaFormulacion: new Date().toISOString().split('T')[0],
          volumenFormulado: "",
          fechaPrimeraDestilacion: new Date().toISOString().split('T')[0],
          volumenOrdinario: "",
          volumenMezcal: "",
          pctAlcOrdinario: "",
          pctAlcMezcal: "",
          volumenColas: "",
          pctAlcColas: ""
        }
      ],
      status: rec.status || "borrador",
      observacionesCorreccion: rec.observacionesCorreccion || rec.observaciones_correccion || ""
    });
    setIsFormOpen(true);
  };

  // Agregar una nueva tina ultra-rápida con prellenado automático inteligente
  const handleAddItem = () => {
    if (formCoccion && kgDisponibles > 0 && formTotalKgMaguey >= kgDisponibles) {
      Swal.fire({
        title: "Consumo de Maguey Completo",
        html: `Ya has asignado el <b>100%</b> del maguey cocido disponible para este lote (<b>${kgDisponibles.toLocaleString()} kg</b>).<br><br>No es posible agregar más tinas a menos que reduzcas los kilogramos de las tinas existentes.`,
        icon: "info",
        confirmButtonColor: "#4A5D23"
      });
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    const currentList = Array.isArray(formData?.items) && formData.items.length > 0 ? formData.items : moliendaItems;
    const nextTinaNum = currentList.length + 1;

    // Calcular saldo remanente disponible para preasignar de inmediato
    const currentUsedKg = currentList.reduce((acc, item) => acc + (parseFloat(String(item.kgMaguey).replace(/[^\d.]/g, '')) || 0), 0);
    const remainingKg = Math.max(0, kgDisponibles - currentUsedKg);
    const suggestedKg = remainingKg > 0 ? String(Math.min(2500, remainingKg)) : "";
    const rawKgNum = parseFloat(suggestedKg) || 0;
    const prevTina = currentList[currentList.length - 1];

    const newItem = {
      id: Date.now(),
      fechaFermentacion: prevTina?.fechaFermentacion || today,
      contenedor: `Tina ${nextTinaNum < 10 ? '0' + nextTinaNum : nextTinaNum}`,
      kgMaguey: suggestedKg,
      equipoMolienda: "",
      equipoFermentacion: "",
      fechaFormulacion: prevTina?.fechaFormulacion || today,
      tipoFormulacion: "espontanea",
      metodoFormulacion: "",
      volumenFormulado: rawKgNum > 0 ? String(Math.round(rawKgNum * 0.6)) : "",
      fechaPrimeraDestilacion: prevTina?.fechaPrimeraDestilacion || today,
      volumenOrdinario: rawKgNum > 0 ? String(Math.round(rawKgNum * 0.22)) : "",
      volumenMezcal: rawKgNum > 0 ? String(Math.round(rawKgNum * 0.22)) : "",
      pctAlcOrdinario: "",
      pctAlcMezcal: "",
      volumenColas: rawKgNum > 0 ? String(Math.round(rawKgNum * 0.03)) : "",
      pctAlcColas: ""
    };

    setFormData(prev => ({
      ...prev,
      items: [...currentList, newItem]
    }));
  };

  const handleRemoveItem = (id) => {
    if (moliendaItems.length <= 1) {
      Swal.fire({
        title: "Atención",
        text: "Debe existir al menos un contenedor / tina en el registro.",
        icon: "info",
        confirmButtonColor: "#4A5D23"
      });
      return;
    }
    setFormData(prev => ({
      ...prev,
      items: (prev.items || []).filter(item => item.id !== id)
    }));
  };

  const handleItemChange = (id, field, value) => {
    setFormData(prev => {
      const currentList = Array.isArray(prev?.items) && prev.items.length > 0 ? prev.items : moliendaItems;
      const updatedList = currentList.map(item => {
        if (item.id !== id) return item;
        const updated = { ...item, [field]: value };

        // Mantener sincronizados volumenOrdinario y volumenMezcal, pctAlcOrdinario y pctAlcMezcal
        if (field === 'volumenOrdinario') {
          updated.volumenMezcal = value;
        } else if (field === 'volumenMezcal') {
          updated.volumenOrdinario = value;
        }
        if (field === 'pctAlcOrdinario') {
          updated.pctAlcMezcal = value;
        } else if (field === 'pctAlcMezcal') {
          updated.pctAlcOrdinario = value;
        }
        if (field === 'volumenColas') {
          updated.volumenMezcal1ra = value;
        } else if (field === 'volumenMezcal1ra') {
          updated.volumenColas = value;
        }
        if (field === 'pctAlcColas') {
          updated.pctAlcMezcal1ra = value;
        } else if (field === 'pctAlcMezcal1ra') {
          updated.pctAlcColas = value;
        }

        // Autocompletar rendimientos de forma instantánea al teclear los kg de maguey
        if (field === 'kgMaguey') {
          const kg = parseFloat(String(value).replace(/[^\d.]/g, '')) || 0;
          if (kg > 0) {
            updated.volumenFormulado = String(Math.round(kg * 0.6));
            updated.volumenOrdinario = String(Math.round(kg * 0.22));
            updated.volumenMezcal = String(Math.round(kg * 0.22));
            if (!item.pctAlcOrdinario && !item.pctAlcMezcal) {
              updated.pctAlcOrdinario = "26.5";
              updated.pctAlcMezcal = "26.5";
            }
            updated.volumenColas = String(Math.round(kg * 0.03));
            updated.volumenMezcal1ra = String(Math.round(kg * 0.03));
            if (!item.pctAlcColas) {
              updated.pctAlcColas = "18.0";
              updated.pctAlcMezcal1ra = "18.0";
            }
          }
        }

        // Sincronizar fechas si no han sido capturadas
        if (field === 'fechaFermentacion' && value) {
          if (!item.fechaFormulacion || item.fechaFormulacion === item.fechaFermentacion) {
            updated.fechaFormulacion = value;
          }
          if (!item.fechaPrimeraDestilacion || item.fechaPrimeraDestilacion === item.fechaFermentacion) {
            updated.fechaPrimeraDestilacion = value;
          }
        }

        return updated;
      });

      return {
        ...prev,
        items: updatedList
      };
    });
  };

  // Cálculos en tiempo real de totales para el formulario
  const formTotalKgMaguey = moliendaItems.reduce((acc, item) => acc + (parseFloat(String(item.kgMaguey).replace(/[^\d.]/g, '')) || 0), 0);
  const formTotalVolFormulado = moliendaItems.reduce((acc, item) => acc + (parseFloat(String(item.volumenFormulado).replace(/[^\d.]/g, '')) || 0), 0);
  const formTotalVolOrdinario = moliendaItems.reduce((acc, item) => acc + (parseFloat(String(item.volumenOrdinario || item.volumenMezcal).replace(/[^\d.]/g, '')) || 0), 0);
  const formTotalVolMezcal = formTotalVolOrdinario;
  const formTotalVolColas = moliendaItems.reduce((acc, item) => acc + (parseFloat(String(item.volumenColas).replace(/[^\d.]/g, '')) || 0), 0);

  // Encontrar la cocción correspondiente al registro seleccionado en el detalle
  const detailCoccion = useMemo(() => {
    if (!selectedRecord) return null;
    return coccionRecords.find(c =>
      c.noProduccion === selectedRecord.noProduccion ||
      c.id === selectedRecord.noProduccion ||
      String(c.rawId) === String(selectedRecord.id_bitacora_coccion) ||
      String(c.idBitacoraCoccion) === String(selectedRecord.id_bitacora_coccion)
    );
  }, [selectedRecord, coccionRecords]);

  // Cocciones disponibles para molienda (permite cualquier estatus: borrador, enviada, aceptada, etc.)
  const availableCocciones = useMemo(() => {
    return (Array.isArray(coccionRecords) ? coccionRecords : []).filter(c => {
      const st = String(c.status || c.statusUI || '').toLowerCase();
      return !st.includes('cancelad');
    });
  }, [coccionRecords]);

  // Cocción seleccionada en el formulario de registro/edición (ÚNICAMENTE SI SE HA SELECCIONADO UNA)
  const formCoccion = useMemo(() => {
    if (!formData?.noProduccion && !formData?.id_bitacora_coccion) {
      return null;
    }
    const match = coccionRecords.find(c =>
      (formData.noProduccion && (c.noProduccion === formData.noProduccion || c.id === formData.noProduccion)) ||
      (formData.id_bitacora_coccion && (String(c.rawId) === String(formData.id_bitacora_coccion) || String(c.idBitacoraCoccion) === String(formData.id_bitacora_coccion) || String(c.id) === String(formData.id_bitacora_coccion)))
    );
    return match || null;
  }, [formData?.noProduccion, formData?.id_bitacora_coccion, coccionRecords]);

  // Saldo y límites de la cocción seleccionada en el formulario
  const coccionBalance = useMemo(() => {
    if (!formCoccion) return { totalKgCocidos: 0, kgUsados: 0, kgDisponibles: 0, isAgotada: false, pctConsumido: 0 };
    return getCoccionMoliendaBalance(formCoccion, moliendaRecords, formData.rawId || formData.id);
  }, [formCoccion, moliendaRecords, formData.rawId, formData.id]);

  const { totalKgCocidos, kgUsados, kgDisponibles, isAgotada: isCoccionAgotada, pctConsumido } = coccionBalance;
  const saldoRemanente = kgDisponibles - formTotalKgMaguey;
  const isConsumoCompleto = Boolean(formCoccion && kgDisponibles > 0 && formTotalKgMaguey >= kgDisponibles);
  const isExcesoKg = Boolean(formCoccion && kgDisponibles > 0 && formTotalKgMaguey > kgDisponibles);

  // Distribuir el saldo disponible equitativamente entre las tinas abiertas
  const handleDistributeBalance = () => {
    if (!formCoccion || kgDisponibles <= 0 || moliendaItems.length === 0) return;
    const splitKg = Math.floor(kgDisponibles / moliendaItems.length);
    const remainder = kgDisponibles - (splitKg * moliendaItems.length);
    setFormData(prev => ({
      ...prev,
      items: (prev.items || []).map((it, idx) => ({
        ...it,
        kgMaguey: String(idx === 0 ? splitKg + remainder : splitKg)
      }))
    }));
  };

  // Autocompletar rendimientos típicos estimados en base a los kg de cada tina
  const handleAutoFillYields = () => {
    setFormData(prev => ({
      ...prev,
      items: (prev.items || []).map(it => {
        const kg = parseFloat(String(it.kgMaguey).replace(/[^\d.]/g, '')) || 0;
        if (kg <= 0) return it;
        const volForm = it.volumenFormulado ? it.volumenFormulado : String(Math.round(kg * 0.6));
        const volOrd = it.volumenOrdinario || it.volumenMezcal ? (it.volumenOrdinario || it.volumenMezcal) : String(Math.round(kg * 0.22));
        const pctAlcOrd = it.pctAlcOrdinario || it.pctAlcMezcal ? (it.pctAlcOrdinario || it.pctAlcMezcal) : "26.5";
        const volCol = it.volumenColas ? it.volumenColas : String(Math.round(kg * 0.03));
        const pctAlcCol = it.pctAlcColas ? it.pctAlcColas : "18.0";
        return {
          ...it,
          volumenFormulado: volForm,
          volumenOrdinario: volOrd,
          volumenMezcal: volOrd,
          pctAlcOrdinario: pctAlcOrd,
          pctAlcMezcal: pctAlcOrd,
          volumenColas: volCol,
          pctAlcColas: pctAlcCol
        };
      })
    }));
  };

  // Duplicar una tina a una nueva tina
  const handleDuplicateItem = (itemToClone) => {
    if (formCoccion && kgDisponibles > 0 && formTotalKgMaguey >= kgDisponibles) {
      Swal.fire({
        title: "Consumo de Maguey Completo",
        html: `Ya has asignado el <b>100%</b> de la materia prima disponible de este lote (<b>${kgDisponibles.toLocaleString()} kg</b>).<br><br>No es posible duplicar ni agregar más tinas.`,
        icon: "info",
        confirmButtonColor: "#4A5D23"
      });
      return;
    }
    const nextTinaNum = moliendaItems.length + 1;
    const today = new Date().toISOString().split('T')[0];
    const newItem = {
      ...itemToClone,
      id: Date.now(),
      contenedor: `Tina ${nextTinaNum < 10 ? '0' + nextTinaNum : nextTinaNum}`,
      fechaFermentacion: itemToClone.fechaFermentacion || today,
      fechaFormulacion: itemToClone.fechaFormulacion || today,
      tipoFormulacion: itemToClone.tipoFormulacion || itemToClone.tipo_formulacion || "espontanea",
      metodoFormulacion: itemToClone.metodoFormulacion || itemToClone.metodo_formulacion || "",
      fechaPrimeraDestilacion: itemToClone.fechaPrimeraDestilacion || today
    };
    setFormData(prev => ({
      ...prev,
      items: [...(prev.items || []), newItem]
    }));
  };

  return (
    <div className="animate-fadeIn">
      {/* HEADER DE HISTORIAL Y CONTROLES */}
      <div className="history-section-header" style={{ flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h2>Historial de Molienda, Fermentación & 1ra Destilación (Sección III)</h2>
          <p className="subtitle-history">
            Bitácora de seguimiento por contenedor, formulación y rendimientos de destilación
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          {/* Filtro por estatus */}
          <select
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            style={{
              padding: "9px 14px",
              borderRadius: "12px",
              border: "1px solid #D1D5DB",
              fontSize: "13px",
              fontWeight: 600,
              color: "#374151",
              backgroundColor: "white",
              outline: "none",
              cursor: "pointer"
            }}
          >
            <option value="todos">Todos los Estatus</option>
            <option value="borrador">Borradores</option>
            <option value="enviada">Enviadas</option>
            <option value="aceptada">Aceptadas</option>
            <option value="rechazada">Rechazadas</option>
            <option value="cancelada">Canceladas</option>
          </select>

          {/* Campo de búsqueda */}
          <div style={{ position: "relative", minWidth: "240px" }}>
            <SearchIcon style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", fontSize: 20 }} />
            <input
              type="text"
              placeholder="Buscar por no. prod, tina, inspector..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{
                width: "100%",
                paddingLeft: "38px",
                paddingRight: search ? "36px" : "12px",
                paddingTop: "9px",
                paddingBottom: "9px",
                borderRadius: "12px",
                border: "1px solid #D1D5DB",
                fontSize: "13px",
                outline: "none",
                backgroundColor: "white"
              }}
            />
            {search && (
              <button
                type="button"
                onClick={() => handleSearchChange("")}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: "#9CA3AF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "4px"
                }}
                title="Limpiar búsqueda"
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </button>
            )}
          </div>

          <button
            onClick={() => {
              if (onOpenNewMolienda) {
                onOpenNewMolienda();
              } else {
                const today = new Date().toISOString().split('T')[0];
                setFormData({
                  id: null,
                  rawId: null,
                  noProduccion: "",
                  id_bitacora_coccion: null,
                  observaciones: "",
                  status: "borrador",
                  items: [
                    {
                      id: Date.now(),
                      fechaFermentacion: today,
                      contenedor: "Tina 01",
                      kgMaguey: "",
                      fechaFormulacion: today,
                      volumenFormulado: "",
                      fechaPrimeraDestilacion: today,
                      volumenOrdinario: "",
                      volumenMezcal: "",
                      pctAlcOrdinario: "",
                      pctAlcMezcal: "",
                      volumenColas: "",
                      pctAlcColas: ""
                    }
                  ]
                });
                setIsFormOpen(true);
              }
            }}
            className="btn-add-new"
          >
            <AddIcon style={{ fontSize: 18 }} />
            <span>Nuevo Registro de Molienda</span>
          </button>
        </div>
      </div>

      {/* TABLA HISTÓRICA O MENSAJE SIN REGISTROS */}
      {filteredRecords.length === 0 ? (
        <div className="empty-history-card">
          <InboxIcon className="empty-history-icon" />
          <h3>{search || statusFilter !== "todos" ? "No se encontraron resultados" : "No hay bitácoras de molienda registradas"}</h3>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "13px" }}>
            {search || statusFilter !== "todos"
              ? "No se hallaron registros coincidentes con los filtros aplicados."
              : "Actualmente no existen registros guardados. Haga clic en 'Nuevo Registro de Molienda' para agregar uno."}
          </p>
        </div>
      ) : (
        <div className="table-responsive-container">
          <table className="custom-table-bitacora">
            <thead>
              <tr>
                <th>No. Producción</th>
                <th>Fecha Bitácora</th>
                <th>Tinas Registradas</th>
                <th>Inspector</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((rec, i) => {
                const fechaBitacora = rec.fechaFermentacion || (rec.items && rec.items[0] ? rec.items[0].fechaFermentacion : '-');
                const numTinas = rec.items && Array.isArray(rec.items) ? rec.items.length : (rec.total_tinas || 0);

                return (
                  <tr key={rec.id || i}>
                    <td className="font-bold-primary">{rec.noProduccion}</td>
                    <td style={{ color: "var(--text-secondary)", fontSize: "12.5px" }}>{fechaBitacora}</td>
                    <td>
                      <span style={{ fontWeight: 700, color: "var(--text-primary)", background: "rgba(74, 93, 35, 0.08)", padding: "4px 10px", borderRadius: "8px", fontSize: "12px" }}>
                        {numTinas} {numTinas === 1 ? 'Tina' : 'Tinas'}
                      </span>
                    </td>
                    <td>
                      <span className="inspector-tag">{rec.inspector || 'Pendiente de V.Bo.'}</span>
                    </td>
                    <td>
                      {renderMoliendaStatusBadge(rec.status || rec.statusUI)}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        <button
                          onClick={() => setSelectedRecord(rec)}
                          title="Ver detalles completos"
                          className="btn-action-icon view"
                        >
                          <VisibilityIcon style={{ fontSize: 16 }} />
                        </button>
                        {!String(rec.status || rec.statusUI || "").toLowerCase().includes("cancelad") && onOpenNewDestilacion && (
                          <button
                            type="button"
                            onClick={() => onOpenNewDestilacion(rec)}
                            title="Registrar Destilación / Refinación para este lote"
                            style={{
                              background: "#2563EB",
                              color: "#FFFFFF",
                              border: "none",
                              borderRadius: "8px",
                              padding: "4px 10px",
                              fontSize: "12px",
                              fontWeight: 700,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              boxShadow: "0 1px 4px rgba(37, 99, 235, 0.2)"
                            }}
                          >
                            <WineBarIcon style={{ fontSize: 15 }} />
                            <span>Destilar</span>
                          </button>
                        )}
                        {(rec.status === "borrador" || rec.status === "rechazado" || rec.status === "rechazada") && (
                          <button
                            onClick={() => handleEditRecord(rec)}
                            title={rec.status === "rechazado" || rec.status === "rechazada" ? "Editar y corregir bitácora" : "Editar Borrador"}
                            className="btn-action-icon edit"
                          >
                            <EditIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                        {rec.status !== "cancelada" && rec.status !== "cancelado" && rec.status !== "aprobado" && rec.status !== "aceptada" && rec.status !== "aceptado" && handleCancelMolienda && (
                          <button
                            onClick={() => handleCancelMolienda(rec)}
                            title="Cancelar Bitácora"
                            className="btn-action-icon cancel"
                          >
                            <CancelIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* CONTROLES DE PAGINACIÓN */}
      {(() => {
        const totalRecs = pagination?.totalRecords ?? filteredRecords.length;
        const totalPages = pagination?.totalPages ?? Math.ceil(totalRecs / (pagination?.limit || 10)) ?? 1;
        const currentPage = pagination?.currentPage || 1;

        if (totalRecs <= 0 && filteredRecords.length === 0) return null;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "16px",
              padding: "12px 18px",
              background: "white",
              borderRadius: "14px",
              border: "1px solid var(--glass-border)",
              boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
              flexWrap: "wrap",
              gap: "12px"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "13px", color: "var(--text-secondary)", fontWeight: 500 }}>
                Mostrando página <strong>{currentPage}</strong> de <strong>{totalPages || 1}</strong> ({totalRecs} registros en total)
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => onPageChange && onPageChange(currentPage - 1)}
                className="btn-secondary-custom"
                style={{
                  padding: "6px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: currentPage <= 1 ? "not-allowed" : "pointer",
                  opacity: currentPage <= 1 ? 0.45 : 1,
                  borderRadius: "8px",
                  border: "1px solid #D1D5DB",
                  backgroundColor: "white"
                }}
              >
                <NavigateBeforeIcon style={{ fontSize: 18 }} /> Anterior
              </button>

              <span
                style={{
                  fontSize: "12.5px",
                  fontWeight: 700,
                  padding: "4px 10px",
                  background: "#F3F4F6",
                  borderRadius: "8px",
                  color: "#374151"
                }}
              >
                {currentPage} / {totalPages || 1}
              </span>

              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => onPageChange && onPageChange(currentPage + 1)}
                className="btn-secondary-custom"
                style={{
                  padding: "6px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                  opacity: currentPage >= totalPages ? 0.45 : 1,
                  borderRadius: "8px",
                  border: "1px solid #D1D5DB",
                  backgroundColor: "white"
                }}
              >
                Siguiente <NavigateNextIcon style={{ fontSize: 18 }} />
              </button>
            </div>
          </div>
        );
      })()}


      {/* =========================================================================
          MODAL 1: DETALLE COMPLETO DE MOLIENDA VIA PORTAL (DISEÑO EXACTO AL REGISTRO)
      ========================================================================= */}
      {selectedRecord && createPortal(
        <div
          className="detail-modal-overlay"
          onClick={() => setSelectedRecord(null)}
        >
          <div
            className="detail-modal-card animate-fadeIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del Modal */}
            <div className="detail-modal-header header-theme-molienda">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <ScienceIcon />
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                    <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 800 }}>
                      Detalle Bitácora de Molienda: {selectedRecord.noProduccion || 'Sin Folio'}
                    </h3>
                    {renderModalHeaderStatusBadge(selectedRecord.status || selectedRecord.statusUI)}
                  </div>
                  <p style={{ margin: "3px 0 0 0", fontSize: "12px", opacity: 0.95 }}>
                    Sección III (A): Seguimiento por contenedor, formulación y 1ra destilación
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                className="modal-close-btn"
                title="Cerrar modal"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Cuerpo del Modal con Scroll */}
            <div className="detail-modal-body modal-body-scrollable" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {/* Banner de Rechazo / Corrección */}
              {(selectedRecord.status === "rechazada" || selectedRecord.status === "rechazado") && (
                <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: "14px", padding: "14px 18px", display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  <ReportProblemIcon style={{ color: "#DC2626", marginTop: "2px" }} />
                  <div>
                    <h4 style={{ margin: "0 0 4px 0", color: "#991B1B", fontSize: "13.5px", fontWeight: 700 }}>
                      Bitácora Rechazada por el Área de Inspección
                    </h4>
                    <p style={{ margin: 0, color: "#7F1D1D", fontSize: "12.5px" }}>
                      <strong>Observaciones del inspector:</strong> {selectedRecord.observacionesCorreccion || selectedRecord.observaciones_correccion || selectedRecord.observacionesInspector || "Se requiere revisión y corrección de la información ingresada."}
                    </p>
                  </div>
                </div>
              )}

              {(() => {
                const items = Array.isArray(selectedRecord.items) ? selectedRecord.items : [];
                const totalKg = items.reduce((acc, it) => acc + (Number(String(it.kgMaguey || 0).replace(/[^\d.]/g, '')) || 0), 0);
                const totalFormulado = items.reduce((acc, it) => acc + (Number(String(it.volumenFormulado || 0).replace(/[^\d.]/g, '')) || 0), 0);
                const totalOrdinario = items.reduce((acc, it) => acc + (Number(String(it.volumenOrdinario || it.volumenMezcal || 0).replace(/[^\d.]/g, '')) || 0), 0);
                const totalMezcal = totalOrdinario;
                const totalColas = items.reduce((acc, it) => acc + (Number(String(it.volumenColas || 0).replace(/[^\d.]/g, '')) || 0), 0);

                const tipoMaguey = selectedRecord.tipoMaguey || detailCoccion?.tipoMaguey || "Agave angustifolia Haw (Espadín)";
                const predio = selectedRecord.predio || detailCoccion?.guiaPredio || detailCoccion?.predio || "-";
                const unidadProduccion = selectedRecord.unidadProduccion || detailCoccion?.unidadProduccion || "-";
                const kgCocidosDisp = selectedRecord.kg_cocidos_disponibles || detailCoccion?.kgMagueyCocido || detailCoccion?.kgMaguey || null;

                return (
                  <>
                    {/* Tarjeta 1: Cocción Base Autorizada */}
                    <section className="detail-card detail-card-accent-left-green" style={{ margin: 0 }}>
                      <header className="detail-card-header">
                        <div className="detail-card-icon-wrapper-green">
                          <GrassIcon />
                        </div>
                        <div className="detail-card-title-wrapper">
                          <h2>Cocción Base Autorizada</h2>
                          <p>Datos del lote de cocción de origen y materia prima</p>
                        </div>
                      </header>

                      <div className="species-grid" style={{ marginBottom: "14px" }}>
                        <div className="integrated-input-box">
                          <span className="integrated-input-label">NO. DE PRODUCCIÓN (LOTE BASE)</span>
                          <div className="integrated-display-value">
                            <strong style={{ fontSize: "14px", color: "var(--agave-green)", fontWeight: 800 }}>
                              {selectedRecord.noProduccion || "-"}
                            </strong>
                          </div>
                        </div>

                        <div className="integrated-input-box">
                          <span className="integrated-input-label">TIPO DE MAGUEY (CATÁLOGO)</span>
                          <div className="integrated-display-value">
                            <span className="chip-maguey" style={{ marginTop: "2px" }}>
                              {tipoMaguey}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="species-grid">
                        <div className="integrated-input-box">
                          <span className="integrated-input-label">MATERIA PRIMA (MAGUEY)</span>
                          <div className="integrated-display-value">
                            <strong style={{ color: "var(--agave-green)", fontWeight: 800 }}>
                              {totalKg > 0 ? `${totalKg.toLocaleString()} kg registrados` : (kgCocidosDisp ? `${kgCocidosDisp}` : "-")}
                            </strong>
                          </div>
                        </div>

                        <div className="integrated-input-box">
                          <span className="integrated-input-label">PREDIO / GUÍA DE TRASLADO</span>
                          <div className="integrated-display-value">
                            {predio}
                          </div>
                        </div>

                        <div className="integrated-input-box">
                          <span className="integrated-input-label">UNIDAD DE PRODUCCIÓN</span>
                          <div className="integrated-display-value">
                            {unidadProduccion}
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Tarjeta 2: Resumen Métrico de Totales */}
                    <section
                      className="detail-card"
                      style={{
                        margin: 0,
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                        gap: "12px",
                        padding: "14px 18px",
                        alignItems: "center",
                        background: "#FFFFFF",
                        border: "1px solid #E2E8F0",
                        borderRadius: "14px"
                      }}
                    >
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Total Maguey</span>
                        <h4 style={{ margin: "3px 0 0 0", color: "var(--agave-green)", fontSize: "18px", fontWeight: 800 }}>
                          {totalKg.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>kg</small>
                        </h4>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Vol. Formulado</span>
                        <h4 style={{ margin: "3px 0 0 0", color: "var(--copper)", fontSize: "18px", fontWeight: 800 }}>
                          {totalFormulado.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>L</small>
                        </h4>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Vol. Ordinario (1ra)</span>
                        <h4 style={{ margin: "3px 0 0 0", color: "#2563EB", fontSize: "18px", fontWeight: 800 }}>
                          {totalOrdinario.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>L</small>
                        </h4>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Vol. Mezcal</span>
                        <h4 style={{ margin: "3px 0 0 0", color: "#7C3AED", fontSize: "18px", fontWeight: 800 }}>
                          {totalColas.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>L</small>
                        </h4>
                      </div>
                    </section>

                    {/* Subheader con contador de Tinas */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "2px", marginBottom: "2px", flexWrap: "wrap", gap: "8px" }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 800, color: "var(--text-primary)" }}>
                          Monitoreo por Tina de Fermentación
                        </h3>
                        <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "var(--text-muted)" }}>
                          Detalle registrado de molienda, formulación y 1ra destilación por contenedor
                        </p>
                      </div>
                      <span style={{ background: "rgba(74, 93, 35, 0.12)", color: "var(--agave-green)", fontWeight: 800, padding: "5px 14px", borderRadius: "10px", fontSize: "12.5px" }}>
                        {items.length} {items.length === 1 ? 'Tina Registrada' : 'Tinas Registradas'}
                      </span>
                    </div>

                    {/* Listado Compacto de Tarjetas de Tinas */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {items.length === 0 ? (
                        <div style={{ padding: "24px", textAlign: "center", background: "#F8FAFC", borderRadius: "12px", border: "1px dashed #CBD5E1", color: "var(--text-muted)" }}>
                          No hay tinas o contenedores registrados en esta bitácora.
                        </div>
                      ) : (
                        items.map((item, index) => {
                          const rawKg = Number(String(item.kgMaguey || 0).replace(/[^\d.]/g, '')) || null;
                          const rawVolForm = Number(String(item.volumenFormulado || 0).replace(/[^\d.]/g, '')) || null;
                          const rawVolOrd = Number(String(item.volumenOrdinario || item.volumenMezcal || 0).replace(/[^\d.]/g, '')) || null;
                          const rawVolMez = rawVolOrd;
                          const rawVolCol = Number(String(item.volumenColas || 0).replace(/[^\d.]/g, '')) || null;

                          return (
                            <div
                              key={item.id || index}
                              style={{
                                background: "#FFFFFF",
                                border: "1px solid #E2E8F0",
                                borderRadius: "12px",
                                padding: "12px 16px",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
                              }}
                            >
                              {/* Header Compacto de la Tina */}
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", paddingBottom: "6px", borderBottom: "1px solid #F1F5F9" }}>
                                <span style={{ background: "rgba(74, 93, 35, 0.12)", color: "var(--agave-green)", fontWeight: 800, padding: "3px 12px", borderRadius: "6px", fontSize: "12.5px" }}>
                                  {item.contenedor || `Tina #${index + 1 < 10 ? '0' + (index + 1) : index + 1}`}
                                </span>
                              </div>

                              {/* Fila 1: Molienda & Fermentación */}
                              <div style={{ marginBottom: "8px" }}>
                                <span style={{ fontSize: "11px", fontWeight: 800, color: "var(--agave-green)", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                                  🌿 1. Molienda & Fermentación
                                </span>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "8px", marginTop: "4px" }}>
                                  <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                                    <span className="integrated-input-label" style={{ fontSize: "9.5px" }}>FECHA MOLIENDA</span>
                                    <div className="integrated-display-value" style={{ fontSize: "12.5px" }}>{item.fechaFermentacion || "-"}</div>
                                  </div>
                                  <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                                    <span className="integrated-input-label" style={{ fontSize: "9.5px" }}>CONTENEDOR / TINA</span>
                                    <div className="integrated-display-value" style={{ fontSize: "12.5px" }}><strong>{item.contenedor || "-"}</strong></div>
                                  </div>
                                  <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                                    <span className="integrated-input-label" style={{ fontSize: "9.5px" }}>KG MAGUEY COCIDO</span>
                                    <div className="integrated-display-value" style={{ fontSize: "12.5px" }}><strong style={{ color: "var(--agave-green)" }}>{rawKg ? `${rawKg.toLocaleString()} kg` : (item.kgMaguey || "-")}</strong></div>
                                  </div>
                                  <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                                    <span className="integrated-input-label" style={{ fontSize: "9.5px" }}>EQUIPO DE MOLIENDA</span>
                                    <div className="integrated-display-value" style={{ fontSize: "12.5px" }}>{item.equipoMolienda || item.equipo_molienda || "-"}</div>
                                  </div>
                                  <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                                    <span className="integrated-input-label" style={{ fontSize: "9.5px" }}>EQUIPO DE FERMENTACIÓN</span>
                                    <div className="integrated-display-value" style={{ fontSize: "12.5px" }}>{item.equipoFermentacion || item.equipo_fermentacion || "-"}</div>
                                  </div>
                                </div>
                              </div>

                              {/* Fila 2: Formulación */}
                              <div style={{ marginBottom: "8px" }}>
                                <span style={{ fontSize: "11px", fontWeight: 800, color: "var(--copper)", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                                  🧪 2. Formulación
                                </span>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "8px", marginTop: "4px" }}>
                                  <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                                    <span className="integrated-input-label" style={{ fontSize: "9.5px" }}>FECHA FORMULACIÓN</span>
                                    <div className="integrated-display-value" style={{ fontSize: "12.5px" }}>{item.fechaFormulacion || "-"}</div>
                                  </div>
                                  <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                                    <span className="integrated-input-label" style={{ fontSize: "9.5px" }}>TIPO DE FORMULACIÓN</span>
                                    <div className="integrated-display-value" style={{ fontSize: "12.5px" }}>
                                      {(item.tipoFormulacion === "inducida" || item.tipo_formulacion === "inducida") ? (
                                        <span style={{ background: "rgba(234, 88, 12, 0.12)", color: "#EA580C", fontWeight: 800, padding: "2px 8px", borderRadius: "6px", fontSize: "12px" }}>
                                          🧪 Inducida: {item.metodoFormulacion || item.metodo_formulacion || "Sin especificar método"}
                                        </span>
                                      ) : (
                                        <span style={{ background: "rgba(4, 120, 87, 0.12)", color: "#047857", fontWeight: 800, padding: "2px 8px", borderRadius: "6px", fontSize: "12px" }}>
                                          🌿 Espontánea (Natural)
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                                    <span className="integrated-input-label" style={{ fontSize: "9.5px" }}>VOL. FORMULADO</span>
                                    <div className="integrated-display-value" style={{ fontSize: "12.5px" }}><strong style={{ color: "var(--copper)" }}>{rawVolForm ? `${rawVolForm.toLocaleString()} L` : (item.volumenFormulado || "-")}</strong></div>
                                  </div>
                                </div>
                              </div>

                              {/* Fila 3: 1ra Destilación (5 columnas en una sola fila) */}
                              <div>
                                <span style={{ fontSize: "11px", fontWeight: 800, color: "#2563EB", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                                  💧 3. 1ra Destilación (Ordinario & Mezcal)
                                </span>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "8px", marginTop: "4px" }}>
                                  <div className="integrated-input-box" style={{ padding: "6px 10px" }}>
                                    <span className="integrated-input-label" style={{ fontSize: "9px" }}>F. 1RA DESTILACIÓN</span>
                                    <div className="integrated-display-value" style={{ fontSize: "12px" }}>{item.fechaPrimeraDestilacion || "-"}</div>
                                  </div>
                                  <div className="integrated-input-box" style={{ padding: "6px 10px" }}>
                                    <span className="integrated-input-label" style={{ fontSize: "9px" }}>VOL. ORDINARIO (A RECTIFICAR)</span>
                                    <div className="integrated-display-value" style={{ fontSize: "12px" }}><strong style={{ color: "#2563EB" }}>{rawVolOrd ? `${rawVolOrd.toLocaleString()} L` : (item.volumenOrdinario || item.volumenMezcal || "-")}</strong></div>
                                  </div>
                                  <div className="integrated-input-box" style={{ padding: "6px 10px" }}>
                                    <span className="integrated-input-label" style={{ fontSize: "9px" }}>% ALC. ORDINARIO</span>
                                    <div className="integrated-display-value" style={{ fontSize: "12px" }}><strong>{item.pctAlcOrdinario || item.pctAlcMezcal ? `${item.pctAlcOrdinario || item.pctAlcMezcal}%` : "-"}</strong></div>
                                  </div>
                                  <div className="integrated-input-box" style={{ padding: "6px 10px" }}>
                                    <span className="integrated-input-label" style={{ fontSize: "9px" }}>VOL. MEZCAL</span>
                                    <div className="integrated-display-value" style={{ fontSize: "12px" }}><strong style={{ color: "#7C3AED" }}>{rawVolCol ? `${rawVolCol.toLocaleString()} L` : (item.volumenColas || "-")}</strong></div>
                                  </div>
                                  <div className="integrated-input-box" style={{ padding: "6px 10px" }}>
                                    <span className="integrated-input-label" style={{ fontSize: "9px" }}>% ALC. MEZCAL</span>
                                    <div className="integrated-display-value" style={{ fontSize: "12px" }}><strong>{item.pctAlcColas ? `${item.pctAlcColas}%` : "-"}</strong></div>
                                  </div>
                                </div>
                              </div>

                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Tarjeta: Observaciones Generales de la Solicitud */}
                    <section className="detail-card detail-card-accent-left-copper" style={{ margin: 0 }}>
                      <header className="detail-card-header">
                        <div className="detail-card-icon-wrapper-copper">
                          <SettingsIcon style={{ fontSize: 20 }} />
                        </div>
                        <div className="detail-card-title-wrapper">
                          <h2>Observaciones Generales de la Solicitud</h2>
                          <p>Notas o contingencias aplicables a todo el lote</p>
                        </div>
                      </header>

                      <div className="integrated-input-box" style={{ marginTop: "8px" }}>
                        <span className="integrated-input-label">OBSERVACIONES</span>
                        <div className="integrated-display-value" style={{ minHeight: "40px", whiteSpace: "pre-wrap", fontWeight: 500, fontSize: "13px", marginTop: "2px" }}>
                          {selectedRecord.observaciones || "Sin observaciones adicionales registradas."}
                        </div>
                      </div>
                    </section>

                    {/* Tarjeta: Dictamen e Inspección UI */}
                    <section className="detail-card" style={{ background: "#FAF9F6", border: "1px solid #EAE6DF", borderRadius: "16px", padding: "18px", margin: 0 }}>
                      <header className="detail-card-header" style={{ marginBottom: "12px" }}>
                        <div className="detail-card-icon-wrapper-copper" style={{ background: "rgba(184, 115, 51, 0.12)", color: "var(--copper)" }}>
                          <FactCheckIcon />
                        </div>
                        <div className="detail-card-title-wrapper">
                          <h2 style={{ fontSize: "14px", fontWeight: 800 }}>Dictamen e Inspección UI</h2>
                          <p style={{ fontSize: "11.5px" }}>Estado de validación oficial y asignación del inspector</p>
                        </div>
                      </header>

                      <div className="species-grid">
                        <div className="integrated-input-box">
                          <span className="integrated-input-label">INSPECTOR ASIGNADO</span>
                          <div className="integrated-display-value">
                            <span className="inspector-tag">
                              {selectedRecord.inspector || 'Pendiente de V.Bo.'}
                            </span>
                          </div>
                        </div>

                        <div className="integrated-input-box">
                          <span className="integrated-input-label">ESTATUS DE LA BITÁCORA</span>
                          <div className="integrated-display-value">
                            {renderMoliendaStatusBadge(selectedRecord.status || selectedRecord.statusUI)}
                          </div>
                        </div>
                      </div>
                    </section>
                  </>
                );
              })()}
            </div>

            {/* Footer del Modal */}
            <div className="detail-modal-footer">
              {(selectedRecord.status === "borrador" || selectedRecord.status === "rechazado" || selectedRecord.status === "rechazada") && (
                <button
                  type="button"
                  onClick={() => {
                    const rec = selectedRecord;
                    setSelectedRecord(null);
                    handleEditRecord(rec);
                  }}
                  className="btn-add-new"
                  style={{ padding: "8px 16px", fontSize: "13px" }}
                >
                  <EditIcon style={{ fontSize: 16 }} />
                  <span>{selectedRecord.status === "rechazado" || selectedRecord.status === "rechazada" ? "Editar y Corregir" : "Editar Borrador"}</span>
                </button>
              )}
              {selectedRecord.status !== "cancelada" && selectedRecord.status !== "cancelado" && selectedRecord.status !== "aprobado" && selectedRecord.status !== "aceptada" && selectedRecord.status !== "aceptado" && handleCancelMolienda && (
                <button
                  type="button"
                  onClick={() => {
                    const rec = selectedRecord;
                    setSelectedRecord(null);
                    handleCancelMolienda(rec);
                  }}
                  className="btn-action-icon cancel"
                  style={{ width: "auto", padding: "8px 16px", height: "auto", borderRadius: "10px", fontWeight: 700, fontSize: "13px", display: "flex", gap: "6px", alignItems: "center" }}
                >
                  <CancelIcon style={{ fontSize: 16 }} />
                  <span>Cancelar</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                className="btn-secondary-custom"
                style={{ padding: "8px 20px", fontSize: "13px" }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}


      {/* =========================================================================
          MODAL 2: FORMULARIO NUEVA / EDITAR BITÁCORA DE MOLIENDA VIA PORTAL
      ========================================================================= */}
      {isFormOpen && createPortal(
        <div
          className="modal-backdrop-custom"
          onClick={() => setIsFormOpen(false)}
        >
          <div
            className="modal-dialog-custom modal-xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "940px" }}
          >
            {/* Header del Modal */}
            <div className="modal-header-custom header-theme-molienda">
              <div className="modal-header-title">
                <div className="icon-badge">
                  <ScienceIcon />
                </div>
                <div>
                  <h3>{formData.rawId || formData.id ? "Editar / Corregir Bitácora de Molienda" : "Nueva Bitácora de Molienda"}</h3>
                  <p>Sección III (A): Registro de Molienda, Fermentación & 1ra Destilación</p>
                </div>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsFormOpen(false)}
                title="Cerrar ventana"
              >
                <CloseIcon />
              </button>
            </div>

            {/* Cuerpo del Modal con Scroll */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (isExcesoKg) {
                  Swal.fire({
                    title: "Exceso de Materia Prima",
                    html: `Has asignado <b>${formTotalKgMaguey.toLocaleString()} kg</b> en las tinas, superando el saldo disponible de <b>${kgDisponibles.toLocaleString()} kg</b> en la cocción <b>${formData.noProduccion}</b> (Exceso: <b>${(formTotalKgMaguey - kgDisponibles).toLocaleString()} kg</b>).<br><br>Por favor ajusta los kilogramos antes de enviar.`,
                    icon: "warning",
                    confirmButtonColor: "#4A5D23"
                  });
                  return;
                }
                if (handleSaveMolienda) {
                  handleSaveMolienda(e, "enviada", formData);
                }
              }}
              className="modal-body-scrollable"
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {/* Banner de Rechazo si está en corrección */}
              {(formData.status === "rechazada" || formData.status === "rechazado") && (
                <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: "14px", padding: "14px 18px", display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  <ReportProblemIcon style={{ color: "#DC2626", marginTop: "2px" }} />
                  <div>
                    <h4 style={{ margin: "0 0 4px 0", color: "#991B1B", fontSize: "13.5px", fontWeight: 700 }}>
                      Bitácora Rechazada por el Área de Inspección
                    </h4>
                    <p style={{ margin: 0, color: "#7F1D1D", fontSize: "12.5px" }}>
                      <strong>Observaciones del inspector:</strong> {formData.observacionesCorreccion || formData.observaciones_correccion || "Por favor revise los datos del registro, realice las correcciones pertinentes y vuelva a enviar la solicitud para revisión."}
                    </p>
                  </div>
                </div>
              )}

              {/* Tarjeta de Lote Base de Cocción */}
              <section className="detail-card detail-card-accent-left-green" style={{ margin: 0 }}>
                <header className="detail-card-header">
                  <div className="detail-card-icon-wrapper-green">
                    <GrassIcon />
                  </div>
                  <div className="detail-card-title-wrapper" style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
                      <h2>Lote de Cocción Autorizado</h2>
                      <span style={{ background: "rgba(4, 120, 87, 0.12)", color: "#047857", fontWeight: 800, padding: "4px 12px", borderRadius: "8px", fontSize: "12px" }}>
                        ⚡ Saldo Disponible: {kgDisponibles.toLocaleString()} kg
                      </span>
                    </div>
                    <p>Seleccione el número de producción base autorizado</p>
                  </div>
                </header>

                <div className="integrated-input-box" style={{ marginBottom: formCoccion ? "14px" : "0" }}>
                  <label className="integrated-input-label" htmlFor="moliendaNoProduccion">
                    NO. DE PRODUCCIÓN (LOTE BASE AUTORIZADO) *
                  </label>
                  <select
                    id="moliendaNoProduccion"
                    value={formData.noProduccion || ""}
                    onChange={e => {
                      const val = e.target.value;
                      const matchedCoccion = availableCocciones.find(c => c.noProduccion === val || c.id === val);
                      const bal = matchedCoccion ? getCoccionMoliendaBalance(matchedCoccion, moliendaRecords, formData.rawId || formData.id) : { kgDisponibles: 0 };
                      const autoKg = bal.kgDisponibles > 0 ? String(Math.min(2500, bal.kgDisponibles)) : "";

                      setFormData(prev => {
                        const currentItems = Array.isArray(prev.items) && prev.items.length > 0 ? prev.items : [{ id: Date.now(), contenedor: "Tina 01" }];
                        const updatedItems = currentItems.map((it, idx) => {
                          if (idx === 0 && (!it.kgMaguey || it.kgMaguey === "")) {
                            return {
                              ...it,
                              kgMaguey: autoKg,
                              volumenFormulado: autoKg ? String(Math.round(Number(autoKg) * 0.6)) : it.volumenFormulado,
                              volumenOrdinario: autoKg ? String(Math.round(Number(autoKg) * 0.22)) : (it.volumenOrdinario || it.volumenMezcal),
                              volumenMezcal: autoKg ? String(Math.round(Number(autoKg) * 0.22)) : (it.volumenOrdinario || it.volumenMezcal),
                              volumenColas: autoKg ? String(Math.round(Number(autoKg) * 0.03)) : it.volumenColas
                            };
                          }
                          return it;
                        });

                        return {
                          ...prev,
                          noProduccion: val,
                          id_bitacora_coccion: matchedCoccion ? (matchedCoccion.rawId || matchedCoccion.id) : null,
                          items: updatedItems
                        };
                      });
                    }}
                    className="integrated-select-control"
                    required
                  >
                    <option value="">-- Seleccionar Bitácora de Cocción (Obligatorio) --</option>
                    {availableCocciones.map((r, idx) => {
                      const { totalKgCocidos: rTotal, kgDisponibles: rDisp, isAgotada: rAgotada } = getCoccionMoliendaBalance(r, moliendaRecords, formData.rawId || formData.id);
                      const isCurrent = formData.noProduccion === r.noProduccion;
                      const disabled = rAgotada && !isCurrent;

                      return (
                        <option key={idx} value={r.noProduccion} disabled={disabled}>
                          {r.noProduccion} ({r.tipoMaguey || "Espadín"}) — {disabled ? '[AGOTADO: 0 kg disp.]' : `Disp: ${rDisp.toLocaleString()} kg de ${rTotal.toLocaleString()} kg`}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {formCoccion && (
                  <>
                    <div className="balance-kpi-grid" style={{ marginTop: "12px", marginBottom: "14px" }}>
                      <div className="balance-kpi-card">
                        <span style={{ fontSize: "10.5px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800 }}>Maguey Cocido Total</span>
                        <h4 style={{ margin: "3px 0 0 0", color: "var(--agave-green)", fontSize: "16px", fontWeight: 800 }}>
                          {totalKgCocidos.toLocaleString()} <small style={{ fontSize: "11px", color: "var(--text-muted)" }}>kg</small>
                        </h4>
                      </div>

                      <div className="balance-kpi-card">
                        <span style={{ fontSize: "10.5px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800 }}>Ya Procesado</span>
                        <h4 style={{ margin: "3px 0 0 0", color: "var(--copper)", fontSize: "16px", fontWeight: 800 }}>
                          {kgUsados.toLocaleString()} <small style={{ fontSize: "11px", color: "var(--text-muted)" }}>kg</small>
                        </h4>
                      </div>

                      <div className={`balance-kpi-card ${!isCoccionAgotada ? 'highlight-available' : ''}`}>
                        <span style={{ fontSize: "10.5px", color: !isCoccionAgotada ? "#047857" : "var(--text-muted)", textTransform: "uppercase", fontWeight: 800 }}>Saldo Disponible</span>
                        <h4 style={{ margin: "3px 0 0 0", color: !isCoccionAgotada ? "#047857" : "#64748B", fontSize: "16px", fontWeight: 800 }}>
                          {kgDisponibles.toLocaleString()} <small style={{ fontSize: "11px", color: !isCoccionAgotada ? "#047857" : "var(--text-muted)" }}>kg</small>
                        </h4>
                      </div>

                      <div className="balance-kpi-card" style={{ borderColor: isExcesoKg ? '#FCA5A5' : '#E2E8F0', background: isExcesoKg ? '#FEF2F2' : '#FFFFFF' }}>
                        <span style={{ fontSize: "10.5px", color: isExcesoKg ? '#DC2626' : 'var(--text-muted)', textTransform: "uppercase", fontWeight: 800 }}>Asignado en Tinas</span>
                        <h4 style={{ margin: "3px 0 0 0", color: isExcesoKg ? '#DC2626' : '#2563EB', fontSize: "16px", fontWeight: 800 }}>
                          {formTotalKgMaguey.toLocaleString()} <small style={{ fontSize: "11px", color: isExcesoKg ? '#DC2626' : 'var(--text-muted)' }}>kg</small>
                        </h4>
                        <div style={{ fontSize: "10.5px", marginTop: "2px", fontWeight: 700, color: saldoRemanente < 0 ? '#DC2626' : '#059669' }}>
                          {saldoRemanente < 0 ? `Exceso: ${Math.abs(saldoRemanente).toLocaleString()} kg` : `Remanente: ${saldoRemanente.toLocaleString()} kg`}
                        </div>
                      </div>
                    </div>

                    {/* Barra de progreso de consumo acumulado */}
                    <div style={{ marginBottom: "14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11.5px", color: "var(--text-muted)", fontWeight: 700 }}>
                        <span>Consumo de Materia Prima Cocida</span>
                        <span>{Math.min(100, Math.round(((kgUsados + formTotalKgMaguey) / (totalKgCocidos || 1)) * 100))}% del Lote</span>
                      </div>
                      <div className="balance-progress-track">
                        <div
                          className={`balance-progress-fill ${isExcesoKg ? 'full' : ''}`}
                          style={{
                            width: `${Math.min(100, Math.round(((kgUsados + formTotalKgMaguey) / (totalKgCocidos || 1)) * 100))}%`,
                            background: isExcesoKg ? '#EF4444' : undefined
                          }}
                        />
                      </div>
                    </div>

                    {/* Alerta de exceso si supera los kg disponibles */}
                    {isExcesoKg && (
                      <div style={{ background: "#FEF2F2", border: "1.5px solid #F87171", borderRadius: "12px", padding: "12px 16px", display: "flex", gap: "10px", alignItems: "center", marginBottom: "14px" }}>
                        <ReportProblemIcon style={{ color: "#DC2626", fontSize: 22 }} />
                        <div style={{ fontSize: "12.5px", color: "#991B1B", fontWeight: 600 }}>
                          <strong>¡Atención: Exceso de Materia Prima!</strong> Has asignado <strong>{formTotalKgMaguey.toLocaleString()} kg</strong> en las tinas, superando el saldo disponible de <strong>{kgDisponibles.toLocaleString()} kg</strong> en este lote (Exceso: <strong>{(formTotalKgMaguey - kgDisponibles).toLocaleString()} kg</strong>). Ajusta los kilogramos antes de enviar.
                        </div>
                      </div>
                    )}

                  </>
                )}
              </section>

              {/* Resumen Métrico de Totales en Tiempo Real con Saldo Disponible */}
              <section
                className="detail-card"
                style={{
                  margin: 0,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(135px, 1fr))",
                  gap: "12px",
                  padding: "14px 18px",
                  alignItems: "center",
                  background: "#FFFFFF",
                  border: "1.5px solid #E2E8F0",
                  borderRadius: "14px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.02)"
                }}
              >
                <div style={{ textAlign: "center", background: "rgba(4, 120, 87, 0.06)", padding: "8px 10px", borderRadius: "10px", border: "1px solid rgba(4, 120, 87, 0.15)" }}>
                  <span style={{ fontSize: "10.5px", color: "#047857", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>⚡ Saldo Disponible</span>
                  <h4 style={{ margin: "3px 0 0 0", color: "#047857", fontSize: "18px", fontWeight: 900 }}>
                    {kgDisponibles.toLocaleString()} <small style={{ fontSize: "12px", color: "#047857" }}>kg</small>
                  </h4>
                </div>
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Maguey en Tinas</span>
                  <h4 style={{ margin: "3px 0 0 0", color: isExcesoKg ? '#DC2626' : 'var(--agave-green)', fontSize: "18px", fontWeight: 800 }}>
                    {formTotalKgMaguey.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>kg</small>
                  </h4>
                </div>
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Vol. Formulado</span>
                  <h4 style={{ margin: "3px 0 0 0", color: "var(--copper)", fontSize: "18px", fontWeight: 800 }}>
                    {formTotalVolFormulado.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>L</small>
                  </h4>
                </div>
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Vol. Ordinario (1ra)</span>
                  <h4 style={{ margin: "3px 0 0 0", color: "#2563EB", fontSize: "18px", fontWeight: 800 }}>
                    {formTotalVolOrdinario.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>L</small>
                  </h4>
                </div>
                <div style={{ textAlign: "center" }}>
                  <span style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.4px" }}>Vol. Mezcal</span>
                  <h4 style={{ margin: "3px 0 0 0", color: "#7C3AED", fontSize: "18px", fontWeight: 800 }}>
                    {formTotalVolColas.toLocaleString()} <small style={{ fontSize: "12px", color: "var(--text-muted)" }}>L</small>
                  </h4>
                </div>
              </section>

              {/* Subheader de Tinas con botones de acción inteligente */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "4px", marginBottom: "4px", flexWrap: "wrap", gap: "12px" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <h3 style={{ margin: 0, fontSize: "15.5px", fontWeight: 800, color: "var(--text-primary)" }}>
                      Tinas de Fermentación & Rendimientos
                    </h3>
                    <span style={{ background: "rgba(74, 93, 35, 0.12)", color: "var(--agave-green)", fontWeight: 800, padding: "3px 10px", borderRadius: "10px", fontSize: "12px" }}>
                      {moliendaItems.length} {moliendaItems.length === 1 ? 'Tina' : 'Tinas'}
                    </span>
                  </div>
                  <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "var(--text-muted)" }}>
                    Capture los kilogramos, fechas de formulación y volúmenes de 1ra destilación por contenedor
                  </p>
                </div>

              </div>

              {/* Listado Compacto de Tarjetas por Contenedor / Tina */}
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {moliendaItems.map((item, index) => (
                  <div
                    key={item.id || index}
                    style={{
                      background: "#FFFFFF",
                      border: "1px solid #E2E8F0",
                      borderRadius: "12px",
                      padding: "12px 16px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
                    }}
                  >
                    {/* Header Compacto de la Tina */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", paddingBottom: "6px", borderBottom: "1px solid #F1F5F9" }}>
                      <span style={{ background: "rgba(74, 93, 35, 0.14)", color: "var(--agave-green)", fontWeight: 800, padding: "3px 12px", borderRadius: "6px", fontSize: "12.5px" }}>
                        {item.contenedor || `Tina #${index + 1 < 10 ? '0' + (index + 1) : index + 1}`}
                      </span>

                      {moliendaItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          title="Eliminar esta tina"
                          style={{ background: "#FEE2E2", color: "#DC2626", border: "1px solid #FCA5A5", borderRadius: "6px", padding: "3px 10px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "11.5px", fontWeight: 700 }}
                        >
                          <DeleteOutlineIcon style={{ fontSize: 15 }} />
                          <span>Eliminar</span>
                        </button>
                      )}
                    </div>

                    {/* Fila 1: Molienda & Fermentación */}
                    <div style={{ marginBottom: "8px" }}>
                      <span style={{ fontSize: "11px", fontWeight: 800, color: "var(--agave-green)", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                        🌿 1. Molienda & Fermentación
                      </span>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "8px", marginTop: "4px" }}>
                        <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                          <label className="integrated-input-label" style={{ fontSize: "9.5px", marginBottom: "2px" }}>FECHA MOLIENDA</label>
                          <input
                            type="date"
                            value={item.fechaFermentacion || ""}
                            onChange={e => handleItemChange(item.id, 'fechaFermentacion', e.target.value)}
                            className="integrated-input-control"
                            style={{ padding: "4px 8px", fontSize: "12px", height: "32px" }}
                          />
                        </div>

                        <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                          <label className="integrated-input-label" style={{ fontSize: "9.5px", marginBottom: "2px" }}>CONTENEDOR / TINA</label>
                          <input
                            type="text"
                            placeholder="Tina 01"
                            value={item.contenedor || ""}
                            onChange={e => handleItemChange(item.id, 'contenedor', e.target.value)}
                            className="integrated-input-control"
                            style={{ padding: "4px 8px", fontSize: "12px", height: "32px" }}
                          />
                        </div>

                        <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                          <label className="integrated-input-label" style={{ fontSize: "9.5px", marginBottom: "2px" }}>KG MAGUEY COCIDO *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            placeholder="2500"
                            value={item.kgMaguey || ""}
                            onChange={e => handleItemChange(item.id, 'kgMaguey', e.target.value)}
                            className="integrated-input-control"
                            style={{ padding: "4px 34px 4px 8px", fontSize: "12px", height: "32px", color: "var(--agave-green)", fontWeight: 800 }}
                            required
                          />
                          <span className="integrated-input-suffix" style={{ fontSize: "11px" }}>kg</span>
                        </div>

                        {/* Selector de Equipo de Molienda */}
                        <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                          <label className="integrated-input-label" style={{ fontSize: "9.5px", marginBottom: "2px" }}>EQUIPO DE MOLIENDA</label>
                          <select
                            value={item.equipoMolienda || ""}
                            onChange={e => handleItemChange(item.id, 'equipoMolienda', e.target.value)}
                            className="integrated-select-control"
                            style={{ padding: "4px 8px", fontSize: "12px", height: "32px" }}
                          >
                            <option value="">-- Seleccionar Equipo de Molienda --</option>
                            {EQUIPOS_MOLIENDA.map(eq => (
                              <option key={eq} value={eq}>{eq}</option>
                            ))}
                            {item.equipoMolienda && !EQUIPOS_MOLIENDA.includes(item.equipoMolienda) && (
                              <option value={item.equipoMolienda}>{item.equipoMolienda}</option>
                            )}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Fila 2: Formulación */}
                    <div style={{ marginBottom: "8px" }}>
                      <span style={{ fontSize: "11px", fontWeight: 800, color: "var(--copper)", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                        🧪 2. Formulación
                      </span>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "8px", marginTop: "4px" }}>
                        {/* Selector de Equipo de Fermentación */}
                        <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                          <label className="integrated-input-label" style={{ fontSize: "9.5px", marginBottom: "2px" }}>EQUIPO DE FERMENTACIÓN</label>
                          <select
                            value={item.equipoFermentacion || ""}
                            onChange={e => handleItemChange(item.id, 'equipoFermentacion', e.target.value)}
                            className="integrated-select-control"
                            style={{ padding: "4px 8px", fontSize: "12px", height: "32px" }}
                          >
                            <option value="">-- Seleccionar Equipo de Fermentación --</option>
                            {EQUIPOS_FERMENTACION.map(eq => (
                              <option key={eq} value={eq}>{eq}</option>
                            ))}
                            {item.equipoFermentacion && !EQUIPOS_FERMENTACION.includes(item.equipoFermentacion) && (
                              <option value={item.equipoFermentacion}>{item.equipoFermentacion}</option>
                            )}
                          </select>
                        </div>
                        <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                          <label className="integrated-input-label" style={{ fontSize: "9.5px", marginBottom: "2px" }}>FECHA FORMULACIÓN</label>
                          <input
                            type="date"
                            value={item.fechaFormulacion || ""}
                            onChange={e => handleItemChange(item.id, 'fechaFormulacion', e.target.value)}
                            className="integrated-input-control"
                            style={{ padding: "4px 8px", fontSize: "12px", height: "32px" }}
                          />
                        </div>

                        <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                          <label className="integrated-input-label" style={{ fontSize: "9.5px", marginBottom: "2px" }}>TIPO DE FORMULACIÓN *</label>
                          <select
                            value={item.tipoFormulacion || item.tipo_formulacion || "espontanea"}
                            onChange={e => {
                              const newType = e.target.value;
                              handleItemChange(item.id, 'tipoFormulacion', newType);
                              if (newType === 'espontanea') {
                                handleItemChange(item.id, 'metodoFormulacion', '');
                              }
                            }}
                            className="integrated-select-control"
                            style={{ padding: "4px 8px", fontSize: "12px", height: "32px", fontWeight: 700 }}
                            required
                          >
                            <option value="espontanea">🌿 Espontánea (Natural)</option>
                            <option value="inducida">🧪 Inducida</option>
                          </select>
                        </div>

                        {(item.tipoFormulacion === "inducida" || item.tipo_formulacion === "inducida") && (
                          <div className="integrated-input-box" style={{ padding: "6px 12px", borderColor: "#F59E0B", background: "rgba(245, 158, 11, 0.04)" }}>
                            <label className="integrated-input-label" style={{ fontSize: "9.5px", marginBottom: "2px", color: "#B45309" }}>MÉTODO DE FORMULACIÓN *</label>
                            <input
                              type="text"
                              placeholder="Ej: Levaduras seleccionadas, nutrientes..."
                              value={item.metodoFormulacion !== undefined && item.metodoFormulacion !== null ? item.metodoFormulacion : (item.metodo_formulacion || "")}
                              onChange={e => handleItemChange(item.id, 'metodoFormulacion', e.target.value)}
                              className="integrated-input-control"
                              style={{ padding: "4px 8px", fontSize: "12px", height: "32px", color: "#92400E", fontWeight: 700 }}
                              required
                            />
                          </div>
                        )}

                        <div className="integrated-input-box" style={{ padding: "6px 12px" }}>
                          <label className="integrated-input-label" style={{ fontSize: "9.5px", marginBottom: "2px" }}>VOL. FORMULADO (L)</label>
                          <input
                            type="number"
                            placeholder="1500"
                            value={item.volumenFormulado || ""}
                            onChange={e => handleItemChange(item.id, 'volumenFormulado', e.target.value)}
                            className="integrated-input-control"
                            style={{ padding: "4px 34px 4px 8px", fontSize: "12px", height: "32px", color: "#2563EB", fontWeight: 800 }}
                          />
                          <span className="integrated-input-suffix" style={{ fontSize: "11px" }}>L</span>
                        </div>
                      </div>
                    </div>

                    {/* Fila 3: 1ra Destilación (5 columnas en una sola fila) */}
                    <div>
                      <span style={{ fontSize: "11px", fontWeight: 800, color: "#2563EB", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                        💧 3. 1ra Destilación (Ordinario & Mezcal)
                      </span>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "8px", marginTop: "4px" }}>
                        <div className="integrated-input-box" style={{ padding: "6px 10px" }}>
                          <label className="integrated-input-label" style={{ fontSize: "9px", marginBottom: "2px" }}>FECHA 1RA DEST.</label>
                          <input
                            type="date"
                            value={item.fechaPrimeraDestilacion || ""}
                            onChange={e => handleItemChange(item.id, 'fechaPrimeraDestilacion', e.target.value)}
                            className="integrated-input-control"
                            style={{ padding: "4px 6px", fontSize: "11.5px", height: "32px" }}
                          />
                        </div>

                        <div className="integrated-input-box" style={{ padding: "6px 10px" }}>
                          <label className="integrated-input-label" style={{ fontSize: "9px", marginBottom: "2px" }}>VOL. ORDINARIO (A RECTIFICAR)</label>
                          <input
                            type="number"
                            placeholder="550"
                            value={item.volumenOrdinario || item.volumenMezcal || ""}
                            onChange={e => {
                              handleItemChange(item.id, 'volumenOrdinario', e.target.value);
                              handleItemChange(item.id, 'volumenMezcal', e.target.value);
                            }}
                            className="integrated-input-control"
                            style={{ padding: "4px 30px 4px 6px", fontSize: "12px", height: "32px", color: "#D97706", fontWeight: 900 }}
                          />
                          <span className="integrated-input-suffix" style={{ fontSize: "10.5px" }}>L</span>
                        </div>

                        <div className="integrated-input-box" style={{ padding: "6px 10px" }}>
                          <label className="integrated-input-label" style={{ fontSize: "9px", marginBottom: "2px" }}>% ALC. ORDINARIO</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            placeholder="26.5"
                            value={item.pctAlcOrdinario || item.pctAlcMezcal || ""}
                            onChange={e => {
                              handleItemChange(item.id, 'pctAlcOrdinario', e.target.value);
                              handleItemChange(item.id, 'pctAlcMezcal', e.target.value);
                            }}
                            className="integrated-input-control"
                            style={{ padding: "4px 26px 4px 6px", fontSize: "12px", height: "32px", color: "#D97706", fontWeight: 800 }}
                          />
                          <span className="integrated-input-suffix" style={{ fontSize: "10.5px" }}>%</span>
                        </div>

                        <div className="integrated-input-box" style={{ padding: "6px 10px" }}>
                          <label className="integrated-input-label" style={{ fontSize: "9px", marginBottom: "2px" }}>VOL. MEZCAL</label>
                          <input
                            type="number"
                            placeholder="75"
                            value={item.volumenColas || item.volumenMezcal1ra || ""}
                            onChange={e => {
                              handleItemChange(item.id, 'volumenColas', e.target.value);
                              handleItemChange(item.id, 'volumenMezcal1ra', e.target.value);
                            }}
                            className="integrated-input-control"
                            style={{ padding: "4px 30px 4px 6px", fontSize: "12px", height: "32px", color: "#7C3AED", fontWeight: 800 }}
                          />
                          <span className="integrated-input-suffix" style={{ fontSize: "10.5px" }}>L</span>
                        </div>

                        <div className="integrated-input-box" style={{ padding: "6px 10px" }}>
                          <label className="integrated-input-label" style={{ fontSize: "9px", marginBottom: "2px" }}>% ALC. MEZCAL</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            placeholder="18.0"
                            value={item.pctAlcColas || item.pctAlcMezcal1ra || ""}
                            onChange={e => {
                              handleItemChange(item.id, 'pctAlcColas', e.target.value);
                              handleItemChange(item.id, 'pctAlcMezcal1ra', e.target.value);
                            }}
                            className="integrated-input-control"
                            style={{ padding: "4px 26px 4px 6px", fontSize: "12px", height: "32px", color: "#7C3AED", fontWeight: 800 }}
                          />
                          <span className="integrated-input-suffix" style={{ fontSize: "10.5px" }}>%</span>
                        </div>
                      </div>
                    </div>

                  </div>
                ))}
              </div>

              {/* Botón Centrado para Agregar Tina */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", margin: "14px 0 6px 0", gap: "6px" }}>
                <button
                  type="button"
                  className="btn-add-new"
                  disabled={isConsumoCompleto}
                  style={{
                    padding: "10px 24px",
                    fontSize: "13px",
                    fontWeight: 700,
                    borderRadius: "10px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: isConsumoCompleto ? "none" : "0 2px 6px rgba(74, 93, 35, 0.2)",
                    opacity: isConsumoCompleto ? 0.55 : 1,
                    cursor: isConsumoCompleto ? "not-allowed" : "pointer",
                    background: isConsumoCompleto ? "#94A3B8" : undefined
                  }}
                  onClick={handleAddItem}
                  title={isConsumoCompleto ? `Materia prima 100% asignada (${kgDisponibles.toLocaleString()} kg). No se pueden agregar más tinas.` : "Agregar otra tina de fermentación"}
                >
                  <AddIcon style={{ fontSize: 18 }} />
                  <span>{isConsumoCompleto ? "Materia Prima 100% Asignada" : "+ Agregar Tina"}</span>
                </button>
                {isConsumoCompleto && (
                  <span style={{ fontSize: "11.5px", color: isExcesoKg ? "#DC2626" : "#059669", fontWeight: 700 }}>
                    {isExcesoKg ? `⚠️ Exceso de ${(formTotalKgMaguey - kgDisponibles).toLocaleString()} kg` : "✓ Consumo de maguey igual al 100% del lote disponible"}
                  </span>
                )}
              </div>

              {/* Observaciones Generales por Solicitud */}
              <section className="detail-card detail-card-accent-left-copper" style={{ margin: 0 }}>
                <header className="detail-card-header">
                  <div className="detail-card-icon-wrapper-copper">
                    <SettingsIcon style={{ fontSize: 20 }} />
                  </div>
                  <div className="detail-card-title-wrapper">
                    <h2>Observaciones Generales de la Solicitud</h2>
                    <p>Notas o contingencias aplicables a todo el lote</p>
                  </div>
                </header>

                <div className="integrated-input-box" style={{ marginTop: "8px" }}>
                  <label className="integrated-input-label" htmlFor="moliendaObservacionesForm">
                    OBSERVACIONES GENERALES
                  </label>
                  <textarea
                    id="moliendaObservacionesForm"
                    value={formData.observaciones || ""}
                    onChange={e => setFormData(prev => ({ ...prev, observaciones: e.target.value }))}
                    className="integrated-input-control"
                    style={{ minHeight: "50px", resize: "none" }}
                    placeholder="Observaciones generales de la solicitud, levaduras, tiempo de fermentación..."
                  />
                </div>
              </section>

              {/* Botones del Modal */}
              <div className="modal-footer-custom" style={{ marginTop: "8px", borderRadius: "12px" }}>
                <button
                  type="button"
                  className="btn-cancel-modal"
                  onClick={() => setIsFormOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn-secondary-custom"
                  style={{ padding: "9px 18px", fontSize: "12.5px" }}
                  onClick={(e) => {
                    if (isExcesoKg) {
                      Swal.fire({
                        title: "Exceso de Materia Prima",
                        html: `Has asignado <b>${formTotalKgMaguey.toLocaleString()} kg</b> en las tinas, superando el saldo disponible de <b>${kgDisponibles.toLocaleString()} kg</b> en la cocción <b>${formData.noProduccion}</b> (Exceso: <b>${(formTotalKgMaguey - kgDisponibles).toLocaleString()} kg</b>).<br><br>Por favor ajusta los kilogramos antes de guardar.`,
                        icon: "warning",
                        confirmButtonColor: "#4A5D23"
                      });
                      return;
                    }
                    if (handleSaveMolienda) {
                      handleSaveMolienda(e, "borrador", formData);
                    }
                  }}
                >
                  Guardar Borrador
                </button>
                <button
                  type="submit"
                  disabled={isExcesoKg}
                  className="btn-submit-modal btn-molienda-submit"
                  style={{
                    opacity: isExcesoKg ? 0.55 : 1,
                    cursor: isExcesoKg ? "not-allowed" : "pointer"
                  }}
                  onClick={(e) => {
                    if (isExcesoKg) {
                      e.preventDefault();
                      Swal.fire({
                        title: "Exceso de Materia Prima",
                        html: `Has asignado <b>${formTotalKgMaguey.toLocaleString()} kg</b> en las tinas, superando el saldo disponible de <b>${kgDisponibles.toLocaleString()} kg</b> en la cocción <b>${formData.noProduccion}</b> (Exceso: <b>${(formTotalKgMaguey - kgDisponibles).toLocaleString()} kg</b>).<br><br>Por favor ajusta los kilogramos antes de enviar.`,
                        icon: "warning",
                        confirmButtonColor: "#4A5D23"
                      });
                      return;
                    }
                    if (handleSaveMolienda) {
                      handleSaveMolienda(e, "enviada", formData);
                    }
                  }}
                  title={isExcesoKg ? `Exceso de ${(formTotalKgMaguey - kgDisponibles).toLocaleString()} kg sobre el saldo disponible. Ajuste las tinas para enviar.` : `Enviar ${moliendaItems.length} Tinas a Revisión`}
                >
                  <span>Guardar y Enviar a Revisión ({moliendaItems.length} Tinas)</span>
                  <ArrowForwardIcon style={{ fontSize: 16 }} />
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default MoliendaSection;
